// ============================================
// ALMACEN/MODALALTAMATERIAL.JS — "Alta de material" (ALMACEN v3, 2026-10-21, A3 + A4)
// ============================================
// El material entra a Central desde: una COMPRA (se confirma en la seccion Compras: ahi queda su costo real y la
// garantia del proveedor), OTRO PROVEEDOR, material EXISTENTE (lo que ya habia) o SOBRANTE de obra. Herramientas
// tambien entran por Central (se dan de alta como activos en Central > Herramientas).
// REQUISITO: ningun producto entra sin imagen (Syscom trae la suya; si no, foto con el celular o la del proveedor).
// A4: los productos serializados se ESCANEAN al darlos de alta (lector de mano o camara); lo que no tiene serie va por
// cantidad. La decision "¿lleva serie?" se toma en la primera alta del producto y ya no cambia.
// Rapido: 3 pasos en una sola pantalla (producto -> cantidad/series -> confirmar), todo en el celular.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchAuth } from '../../utils/sesion';
import { API_BASE } from '../../config/api';
import SubirImagen from '../SubirImagen';
import EscanerSeries from './EscanerSeries';
import { Modal, C, PP, inputBase, labelStyle, btnCancelar, btnOk, Foto, fmtCant, unidadCorta } from './ui';

const ORIGENES = [
  { id: 'compra',         label: '🧾 Compra (factura o ticket)', nota: 'Se registra en la sección Compras: entra con su costo real y la garantía del proveedor.' },
  { id: 'otro_proveedor', label: '🏪 Otro proveedor (sin factura en Compras)', nota: 'Material comprado fuera; entra con el costo que captures.' },
  { id: 'existente',      label: '📦 Material existente (lo que ya había)', nota: 'Alta inicial de lo que ya estaba en la bodega.' },
  { id: 'sobrante',       label: '↩️ Sobrante de obra', nota: 'Lo que regresó de una instalación sin pasar por un expediente.' },
];
const FUENTES = { syscom_producto: { label: 'Syscom', color: '#5c9adb' }, beep_producto: { label: 'Mis Productos', color: '#5cd4b0' }, articulo_interno: { label: 'Interno', color: '#7c5cdb' } };

function FilaProducto({ p, seleccionado, onClick, extra }) {
  const f = FUENTES[p.tipo] || FUENTES.beep_producto;
  return (
    <button type="button" onClick={onClick} style={{ width: '100%', textAlign: 'left', display: 'flex', gap: 10, alignItems: 'center', padding: '8px 10px', minHeight: 48, background: seleccionado ? '#eef5fd' : '#fff', border: `1.5px solid ${seleccionado ? C.principal : C.borde}`, borderRadius: 10, cursor: 'pointer', fontFamily: PP }}>
      <div style={{ width: 6, alignSelf: 'stretch', background: f.color, borderRadius: 3, flexShrink: 0 }} />
      <Foto src={p.imagen_url} tam={36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.gris, overflowWrap: 'anywhere' }}>{p.nombre}</div>
        <div style={{ fontSize: 11, color: C.grisClaro }}>{f.label}{p.marca ? ` · ${p.marca}` : ''}{p.modelo ? ` · ${p.modelo}` : ''}{!p.imagen_url ? ' · sin imagen' : ''}</div>
      </div>
      {extra && <div style={{ fontSize: 12, fontWeight: 700, color: C.oscuro, flexShrink: 0 }}>{extra}</div>}
    </button>
  );
}

export default function ModalAltaMaterial({ existencias = [], veCostos, preseleccion, onCerrar, onHecho, onIrACompras }) {
  const [origen, setOrigen] = useState('existente');
  const [producto, setProducto] = useState(preseleccion || null);
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [cantidad, setCantidad] = useState('');
  const [costo, setCosto] = useState('');
  const [nota, setNota] = useState('');
  const [imagenUrl, setImagenUrl] = useState('');
  const [llevaSerie, setLlevaSerie] = useState(null); // null = por decidir
  const [series, setSeries] = useState([]);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  // La existencia previa del producto (si ya esta en Central) decide serie e imagen.
  const previa = useMemo(() => producto ? existencias.find(e => e.tipo === producto.tipo && String(e.referencia_id) === String(producto.referencia_id)) : null, [producto, existencias]);
  const serieDecidida = previa && previa.requiere_serie != null ? !!previa.requiere_serie : llevaSerie;
  const tieneImagen = !!((producto && producto.imagen_url) || (previa && previa.imagen_url) || imagenUrl);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setResultados([]); setBuscando(false); return undefined; }
    let vivo = true; setBuscando(true);
    const h = setTimeout(async () => {
      try {
        const r = await fetchAuth(`${API_BASE}/api/tienda/buscar?limit=12&q=${encodeURIComponent(t)}`);
        const d = await r.json().catch(() => ({}));
        if (!vivo) return;
        setResultados(r.ok && d.ok ? (d.productos || []).map(p => ({
          tipo: p.tipo === 'syscom_producto' ? 'syscom_producto' : 'beep_producto', referencia_id: String(p.referencia_id),
          nombre: p.nombre, marca: p.marca || null, modelo: p.modelo || null, imagen_url: p.imagen_url || null, costo_catalogo: p.costo_mxn != null ? p.costo_mxn : null,
        })).filter(p => p.tipo === 'syscom_producto' || /^\d+$/.test(p.referencia_id)) : []);
      } catch (e) { if (vivo && e.code !== 'SESION_EXPIRADA') setResultados([]); }
      finally { if (vivo) setBuscando(false); }
    }, 300);
    return () => { vivo = false; clearTimeout(h); };
  }, [q]);

  const yaEnInventario = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    return existencias.filter(e => e.tipo !== 'articulo_interno' && `${e.nombre} ${e.marca || ''} ${e.modelo || ''}`.toLowerCase().includes(t)).slice(0, 5);
  }, [q, existencias]);

  const elegir = (p) => { setProducto(p); setSeries([]); setLlevaSerie(null); setImagenUrl(''); if (veCostos && p.costo_catalogo != null && costo === '') setCosto(String(p.costo_catalogo)); };

  const validarSerie = async (s) => {
    try {
      const r = await fetchAuth(`${API_BASE}/api/inventario/series/validar`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ serie: s, tipo: producto.tipo, referencia_id: producto.referencia_id, lista: series }) });
      const d = await r.json().catch(() => ({}));
      return r.ok && d.ok ? null : (d.error || 'Serie rechazada');
    } catch (e) { return e.code === 'SESION_EXPIRADA' ? 'Sesión expirada' : 'No se pudo validar la serie'; }
  };

  const porSerie = serieDecidida === true;
  const cantOk = porSerie ? /^\d+$/.test(cantidad.trim()) && Number(cantidad) > 0 : /^\d+(\.\d+)?$/.test(cantidad.trim()) && Number(cantidad) > 0;
  const costoOk = veCostos ? /^\d+(\.\d+)?$/.test(costo.trim().replace(/,/g, '')) : true;
  const seriesOk = !porSerie || (cantOk && series.length === Number(cantidad));
  const listo = producto && origen !== 'compra' && serieDecidida != null && cantOk && costoOk && tieneImagen && seriesOk && !enviando;

  const confirmar = async () => {
    if (!listo) return;
    setEnviando(true); setError('');
    try {
      const motivo = origen === 'existente' ? 'alta_inicial' : origen;
      const r = await fetchAuth(`${API_BASE}/api/inventario/entradas`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: producto.tipo, referencia_id: producto.referencia_id, cantidad: cantidad.trim(), ...(veCostos ? { costo_unitario: costo.trim().replace(/,/g, '') } : {}), motivo, nota: nota.trim() || null, destino: 'central',
          imagen_url: imagenUrl || null, requiere_serie: porSerie, series: porSerie ? series : [] }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || 'No se pudo registrar la entrada');
      onHecho(`Entró a Central: ${fmtCant(cantidad)} ${unidadCorta(d.existencia && d.existencia.unidad)} de "${producto.nombre}"${porSerie ? ` con ${series.length} serie${series.length === 1 ? '' : 's'}` : ''}. Existencia: ${fmtCant(d.existencia.cantidad)}.`);
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); setEnviando(false); }
  };

  const org = ORIGENES.find(o => o.id === origen);

  return (
    <Modal titulo="📥 Alta de material" onCerrar={onCerrar} pie={<>
      <button onClick={onCerrar} style={btnCancelar}>Cancelar</button>
      {origen === 'compra'
        ? <button onClick={() => { if (onIrACompras) onIrACompras(); }} style={btnOk(true)}>Ir a Compras</button>
        : <button onClick={confirmar} disabled={!listo} data-alta="confirmar" style={btnOk(listo)}>{enviando ? 'Guardando…' : 'Dar de alta en Central'}</button>}
    </>}>
      <label style={labelStyle}>¿De dónde entra?</label>
      <select value={origen} onChange={(e) => setOrigen(e.target.value)} style={inputBase} data-alta="origen">
        {ORIGENES.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 4 }}>{org && org.nota}</div>
      {origen === 'compra' ? (
        <div style={{ marginTop: 10, background: '#eef5fd', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, color: C.oscuro, lineHeight: 1.5 }}>
          Las compras entran por <strong>Compras</strong>: Traer de Syscom (factura), XML del CFDI u otro proveedor con foto del ticket. Ahí también se escanean las series y se sube la foto del producto si el catálogo no la trae.
        </div>
      ) : (
        <>
          {!producto ? (
            <>
              <label style={labelStyle}>Producto del catálogo</label>
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} style={inputBase} placeholder="Modelo, nombre o marca…" inputMode="search" enterKeyHint="search" autoComplete="off" data-alta="buscar" />
              <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 6 }}>{q.trim().length < 2 ? 'Escribe al menos 2 letras (Syscom o Mis Productos). Las herramientas se dan de alta en Central › Herramientas.' : buscando ? 'Buscando…' : `${resultados.length} resultado${resultados.length === 1 ? '' : 's'}`}</div>
              {yaEnInventario.length > 0 && (<><div style={{ ...labelStyle, color: C.verde }}>Ya en Central</div><div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{yaEnInventario.map(e => <FilaProducto key={`inv-${e.id}`} p={e} onClick={() => elegir({ tipo: e.tipo, referencia_id: e.referencia_id, nombre: e.nombre, marca: e.marca, modelo: e.modelo, imagen_url: e.imagen_url })} extra={`${fmtCant(e.cantidad)} ${unidadCorta(e.unidad)}`} />)}</div></>)}
              {resultados.length > 0 && (<><div style={labelStyle}>Catálogo</div><div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{resultados.map(p => <FilaProducto key={`${p.tipo}-${p.referencia_id}`} p={p} onClick={() => elegir(p)} />)}</div></>)}
            </>
          ) : (
            <>
              <label style={labelStyle}>Producto</label>
              <FilaProducto p={producto} seleccionado onClick={() => { if (!preseleccion) { setProducto(null); setCosto(''); } }} extra={preseleccion ? null : 'cambiar'} />

              {/* IMAGEN obligatoria */}
              {!tieneImagen && (
                <div style={{ marginTop: 10, background: '#fffbeb', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontSize: 12.5, color: C.ambar, fontWeight: 700 }}>📷 Este producto no tiene imagen: toma una foto o sube la del proveedor.</div>
                  <div style={{ fontSize: 11, color: C.grisClaro, marginBottom: 6 }}>Ningún producto entra sin imagen.</div>
                  <SubirImagen folder="beep-os/almacen" aspectRatio="square" label="" capture="environment" onChange={(url) => setImagenUrl(url)} />
                </div>
              )}
              {imagenUrl && <div style={{ fontSize: 12, color: C.verde, marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}><Foto src={imagenUrl} tam={44} /> Imagen lista.</div>}

              {/* ¿Lleva serie? */}
              <label style={labelStyle}>¿Lleva número de serie?</label>
              {previa && previa.requiere_serie != null ? (
                <div style={{ fontSize: 12.5, color: C.gris }}>{previa.requiere_serie ? '🔢 Sí: este producto va por serie (ya decidido en su primera alta).' : '🔢 No: va por cantidad (ya decidido en su primera alta).'}</div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{ v: true, t: 'Sí, cada pieza tiene S/N' }, { v: false, t: 'No, va por cantidad' }].map(o => (
                    <button key={String(o.v)} type="button" onClick={() => { setLlevaSerie(o.v); if (!o.v) setSeries([]); }} data-alta={o.v ? 'serie-si' : 'serie-no'} style={{ flex: 1, minHeight: 44, borderRadius: 10, border: `1.5px solid ${llevaSerie === o.v ? C.principal : C.borde}`, background: llevaSerie === o.v ? '#eef5fd' : '#fff', color: C.oscuro, fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: PP }}>{o.t}</button>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 4 }}>Cámaras, grabadores, equipos: con serie. Tornillería, tubería, cable, consumibles: por cantidad.</div>

              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>{porSerie ? 'Piezas' : 'Cantidad'}</label>
                  <input type="text" inputMode={porSerie ? 'numeric' : 'decimal'} value={cantidad} onChange={(e) => setCantidad(e.target.value)} style={inputBase} placeholder="0" data-alta="cantidad" />
                </div>
                {veCostos && (
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Costo unitario (MXN)</label>
                    <input type="text" inputMode="decimal" value={costo} onChange={(e) => setCosto(e.target.value)} style={inputBase} placeholder="0.00" data-alta="costo" />
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 4 }}>{veCostos ? 'El costo se guarda tal cual, con todos sus decimales; el promedio lo calcula el sistema sin redondear.' : 'Entra al costo promedio vigente. La primera entrada de un producto nuevo la registra compras o dirección.'}</div>

              {porSerie && (
                <div style={{ marginTop: 10 }}>
                  <EscanerSeries series={series} onAgregar={(s) => setSeries(v => [...v, s])} onQuitar={(s) => setSeries(v => v.filter(x => x !== s))} validar={validarSerie} tope={cantOk ? Number(cantidad) : null} etiqueta="Escanea la serie de cada pieza" />
                  {cantOk && series.length !== Number(cantidad) && <div style={{ fontSize: 11.5, color: C.ambar, marginTop: 4, fontWeight: 600 }}>Faltan {Number(cantidad) - series.length} serie{Number(cantidad) - series.length === 1 ? '' : 's'} por escanear.</div>}
                </div>
              )}
              <label style={labelStyle}>Nota (opcional)</label>
              <input value={nota} onChange={(e) => setNota(e.target.value)} style={inputBase} placeholder="Proveedor, obra, observaciones…" />
            </>
          )}
        </>
      )}
      {error && <div style={{ marginTop: 10, background: '#fde8e8', color: '#b02020', borderRadius: 10, padding: '8px 10px', fontSize: 12 }}>{error}</div>}
    </Modal>
  );
}
